import React, { useCallback, useEffect } from "react";
import styled, { css } from "styled-components";
import SVG from "react-inlinesvg";
import { toREM, toVWDesktop, toVWMobile } from "../../helpers/styles";
import { Link } from "../shared/Link";
import Collapsible from "react-collapsible";
import instagram from "../../assets/images/social_icons/instagram.svg";
import youtube from "../../assets/images/social_icons/youtube.svg";
import facebook from "../../assets/images/social_icons/facebook.svg";
import linkedin from "../../assets/images/social_icons/linkedIn_white_icon.svg";
import x from "../../assets/images/social_icons/x_white_icon.svg";
import threads from "../../assets/images/social_icons/threads_white_icon.svg";
import desktopFooterLogo from "../../assets/images/desktop_footer_logo.svg";
import footerMenuIcon from "../../assets/images/footer_menu_icon.svg";
import { useFooterData } from "../../hooks/useFooterData";
import { BREAKPOINTS } from "../../helpers/theme";
import LanguagePicker from "../LanguagePicker";
import SectionLines from "../SectionLines/SectionLines";

type SocialType = {
  social_title: string;
  instagram_url: string;
  youtube_url: string;
  facebook_url: string;
  threads_url: string;
  twitter_url: string;
  linkedin_url: string;
};

type SectionType = {
  section_title: string;
  options: Array<{ label: string; url: string }>;
};

type SubFooterType = {
  copyright: string;
  links: Array<{ label: string; url: string }>;
};

type FooterProps = {
  isRTL?: boolean;
};

export type FooterData = {
  isPublished?: boolean;
  social: SocialType;
  sections: Array<SectionType>;
  sub_footer: SubFooterType;
};

const renderSections = (sections: Array<SectionType>) => {
  return (
    <>
      {(sections || []).map((section) => {
        const { section_title, options } = section;
        return (
          <LinksColumn key={section_title}>
            <MobileContainer
              transitionTime={300}
              trigger={
                <LinkTitle>
                  {section_title}
                  <CollapseIcon src={footerMenuIcon} alt="Expand" />
                </LinkTitle>
              }
              triggerWhenOpen={
                <LinkTitle>
                  {section_title}
                  <CollapseIcon src={footerMenuIcon} alt="Collapse" $open />
                </LinkTitle>
              }
            >
              <LinkItems>
                {options.map((option) => {
                  const { label, url } = option;
                  return (
                    <LinkItem key={label} to={url || ""}>
                      {label}
                    </LinkItem>
                  );
                })}
              </LinkItems>
            </MobileContainer>
            <DesktopContainer>
              <LinkTitle>{section_title}</LinkTitle>
              <LinkItems>
                {options.map((option) => {
                  const { label, url } = option;
                  return (
                    <LinkItem key={label} to={url || ""}>
                      {label}
                    </LinkItem>
                  );
                })}
              </LinkItems>
            </DesktopContainer>
          </LinksColumn>
        );
      })}
    </>
  );
};

const renderLegals = (links: Array<{ label: string; url: string }>) => {
  return (
    <Legals>
      {(links || [])
        .filter((l) => !l.isCookie)
        .map((l) => {
          return (
            <LegalItemLink key={l.label} to={l?.url || ""}>
              {l.label}
            </LegalItemLink>
          );
        })}
      <LegalItem id="teconsent" />
    </Legals>
  );
};

const FooterV2 = ({ isRTL = false }: FooterProps): JSX.Element => {
  const data: FooterData = useFooterData();
  const { social, sections, sub_footer, isPublished } = data;
  const {
    social_title,
    instagram_url,
    youtube_url,
    facebook_url,
    threads_url,
    twitter_url,
    linkedin_url,
  } = social;

  if (!isPublished) return null;

  const getCookie = useCallback(() => {
    try {
      return sub_footer?.links?.find((l) => l.isCookie);
    } catch (e) {
      console.log(e);
      return null;
    }
  }, [sub_footer?.links]);

  useEffect(() => {
    const tId = setInterval(() => {
      const cookie = getCookie();
      const element = document?.querySelector("#teconsent>a");
      if (element) {
        element.innerHTML = cookie?.label || "";
        clearInterval(tId);
      }
    }, 500);
    return () => {
      try {
        clearInterval(tId);
      } catch (e) {
        console.log(e);
      }
    };
  }, [getCookie]);

  const hasSocialLinks = Boolean(
    instagram_url ||
      youtube_url ||
      facebook_url ||
      threads_url ||
      twitter_url ||
      linkedin_url,
  );

  const hasSocialSection = Boolean(hasSocialLinks || social_title);

  return (
    <FooterContainer>
      <MainContainer>
        <MainInner>
          <SocialColumn>
            {social_title && <SocialTitle>{social_title}</SocialTitle>}
            {hasSocialLinks && (
              <SocialIcons>
                {instagram_url && (
                  <SocialLink
                    href={instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Beyond Meat Instagram"
                  >
                    <SocialSvg src={instagram} />
                  </SocialLink>
                )}
                {youtube_url && (
                  <SocialLink
                    href={youtube_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Beyond Meat YouTube"
                  >
                    <SocialSvg src={youtube} />
                  </SocialLink>
                )}
                {facebook_url && (
                  <SocialLink
                    href={facebook_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Beyond Meat Facebook"
                  >
                    <SocialSvg src={facebook} />
                  </SocialLink>
                )}
                {threads_url && (
                  <SocialLink
                    href={threads_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Beyond Meat Threads"
                  >
                    <SocialSvg src={threads} />
                  </SocialLink>
                )}
                {twitter_url && (
                  <SocialLink
                    href={twitter_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Beyond Meat X"
                  >
                    <SocialSvg src={x} />
                  </SocialLink>
                )}
                {linkedin_url && (
                  <SocialLink
                    href={linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Beyond Meat LinkedIn"
                  >
                    <SocialSvg src={linkedin} />
                  </SocialLink>
                )}
              </SocialIcons>
            )}
            <DesktopOnly>
              <LanguagePickerContainer removeMargin={!hasSocialSection}>
                <LanguagePicker />
              </LanguagePickerContainer>
            </DesktopOnly>
          </SocialColumn>
          <MobileOnly>
            <LanguagePickerContainer>
              <LanguagePicker />
            </LanguagePickerContainer>
          </MobileOnly>
          <SectionsContainer>{renderSections(sections)}</SectionsContainer>
        </MainInner>
      </MainContainer>
      <Divider />
      <LogoSection>
        <LogoInner>
          <LogoImage src={desktopFooterLogo} alt="Go Beyond" />
        </LogoInner>
      </LogoSection>
      <Divider />
      <LegalContainer>
        <LegalInner>
          {sub_footer.copyright}
          {renderLegals(sub_footer.links)}
        </LegalInner>
      </LegalContainer>
    </FooterContainer>
  );
};

const FooterContainer = styled.footer`
  ${SectionLines("#F6F1E7")};
  width: 100%;
  background-color: #012604;
  border-top: 1px solid #6ab437;
  color: #f6f1e7;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  text-align: ${(props) => (props.theme.isRTL ? "right" : "left")};
  text-transform: none;
  direction: ${(props) => (props.theme.isRTL ? "rtl" : "ltr")};
  overflow: hidden;

  &::before,
  &::after {
    width: 1px;
    background: #6ab437;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    border-top: 1px solid rgba(246, 241, 231, 0.65);
    &::before,
    &::after {
      background: rgba(246, 241, 231, 0.65);
    }
  }
`;

const MainContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const MainInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    max-width: ${toVWDesktop(1440)}vw;
    margin: 0 auto;
    flex-direction: row;
    gap: 0;
    align-items: flex-start;
    padding: ${toVWDesktop(40)}vw ${toVWDesktop(80)}vw;
  }
`;

const SocialColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${toVWMobile(20)}vw;
  padding: ${toVWMobile(20)}vw ${toVWMobile(32)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toVWDesktop(453)}vw;
    gap: 0;
    padding: 0;
    margin-right: 0;
    margin-left: ${(props) => (props.theme.isRTL ? `${toVWDesktop(24)}vw` : 0)};
  }
`;

const SocialTitle = styled.h2`
  font-size: ${toREM(16)}rem;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  color: #f6f1e7;
  font-weight: 600;
  letter-spacing: 0;
  line-height: normal;
  margin: 0;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(20)}rem;
    margin-bottom: ${toVWDesktop(20)}vw;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  margin: 0;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: auto;
    justify-content: flex-start;
    gap: ${toVWDesktop(36)}vw;
    margin-bottom: ${toVWDesktop(48)}vw;
  }
`;

const SocialLink = styled.a`
  color: #f6f1e7;
  align-items: center;
  justify-content: center;
  transition: color 200ms ease;
  &:hover {
    color: #b5dd71;
  }
`;
const SocialSvg = styled(SVG)`
  width: ${toVWMobile(24)}vw;
  height: ${toVWMobile(24)}vw;

  color: inherit;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toVWDesktop(24)}vw;
    height: ${toVWDesktop(24)}vw;
  }

  svg {
    width: 100%;
    height: 100%;
  }

  svg * {
    fill: currentColor;
  }
`;
const CollapseIcon = styled.img<{ $open?: boolean }>`
  width: ${toVWMobile(16)}vw;
  height: ${toVWMobile(16)}vw;
  object-fit: contain;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "none")};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

const LinksColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    flex: 1;
    min-width: 0;
  }
`;

const SectionsContainer = styled.div`
  width: 100%;
  padding: ${toVWMobile(20)}vw ${toVWMobile(32)}vw;
  display: flex;
  flex-direction: column;
  gap: ${toVWMobile(16)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    border-top: none;
    padding: 0;
    gap: 0;
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const LinkTitle = styled.h3`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #f6f1e7;
  font-weight: 600;
  letter-spacing: 0;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(16)}rem;
  line-height: normal;
  margin-top: 0;
  margin-bottom: 0;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(20)}rem;
    img {
      display: none;
    }
  }
`;

const LinkItems = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${toVWMobile(16)}vw 0 ${toVWMobile(8)}vw;
  gap: ${toVWMobile(12)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${toVWDesktop(40)}vw 0 0;
    gap: ${toVWDesktop(16)}vw;
  }
`;

const LinkItem = styled(Link)`
  color: #f6f1e7;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(14)}rem;
  font-weight: 500;
  line-height: normal;
  text-align: start;
  text-transform: none;
  text-decoration: none;
  margin: 0;
  opacity: 0.95;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(14)}rem;
  }
  &:hover,
  &:active {
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
  }

  &:focus-visible {
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
    outline: none;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #6ab437;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    background: rgba(246, 241, 231, 0.65);
  }
`;

const LogoSection = styled.div`
  display: flex;
  justify-content: center;
  padding: ${toVWMobile(32)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    justify-content: flex-start;
    padding: ${toVWDesktop(40)}vw ${toVWDesktop(80)}vw;
  }
`;

const LogoInner = styled.div`
  width: 100%;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    max-width: ${toVWDesktop(1440)}vw;
    margin: 0 auto;
  }
`;

const LogoImage = styled.img`
  width: ${toVWMobile(329)}vw;
  max-width: 100%;
  height: auto;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: 100%;
    max-width: ${toVWDesktop(1280)}vw;
  }
`;

const LegalContainer = styled.div`
  padding: ${toVWMobile(16)}vw ${toVWMobile(32)}vw;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: ${toVWDesktop(40)}vw ${toVWDesktop(80)}vw;
  }
`;

const LegalInner = styled.div`
  display: flex;
  direction: ${(props) => (props.theme.isRTL ? "rtl" : "ltr")};
  flex-direction: column;
  align-items: flex-start;
  gap: ${toVWMobile(12)}vw;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  color: #f6f1e7;
  font-size: ${toREM(12)}rem;
  font-weight: 600;
  line-height: normal;
  opacity: 0.7;
  text-transform: none;
  width: 100%;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toVWDesktop(680)}vw;
    max-width: 100%;
    flex-direction: row;
    align-items: center;
    gap: ${toVWDesktop(24)}vw;
  }
`;

const Legals = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr;
  column-gap: ${toVWMobile(16)}vw;
  row-gap: ${toVWMobile(2)}vw;
  align-items: start;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: flex;
    width: auto;
    flex-direction: row;
    align-items: center;
    gap: ${toVWDesktop(24)}vw;
  }
`;

const LegalItemLink = styled(Link)`
  color: #f6f1e7;
  margin-top: 0;
  font-size: ${toREM(12)}rem;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  line-height: normal;
  text-decoration: none;
  &:hover,
  &:active {
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
  }

  &:focus-visible {
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
    outline: none;
  }
`;

const LegalItem = styled.div`
  color: #f6f1e7;
  margin-top: 0;
  font-size: ${toREM(12)}rem;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  line-height: normal;
  text-decoration: none;
`;

const MobileContainer = styled(Collapsible)`
  span,
  .Collapsible__trigger {
    width: 100%;
  }
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

const DesktopContainer = styled.div`
  display: none;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: block;
    width: ${toVWDesktop(206)}vw;
    row-gap: 0;
  }
`;

const LanguagePickerContainer = styled.div<{ removeMargin?: boolean }>`
  width: 100%;
  max-width: 100%;
  border-top: 1px solid #6ab437;
  border-bottom: 1px solid #6ab437;
  padding: ${toVWMobile(20)}vw ${toVWMobile(32)}vw;
  ${({ removeMargin }) =>
    !removeMargin &&
    css`
      margin-top: 0;
      @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
        margin-top: 0;
      }
    `}

  > div > div:first-child {
    color: #f6f1e7;
    font-weight: 600;
    font-size: ${toREM(16)}rem;
    line-height: normal;
  }

  > div button {
    position: relative;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    border: 1px solid #f6f1e7;
    border-radius: ${toVWMobile(431)}vw;
    background: transparent;
    padding: ${toVWMobile(12)}vw ${toVWMobile(16)}vw ${toVWMobile(12)}vw
      ${toVWMobile(20)}vw;
    min-height: ${toVWMobile(44)}vw;
    gap: ${toVWMobile(8)}vw;

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid #f6f1e7;
      outline-offset: 2px;
    }
  }

  > div button img:first-child {
    display: none;
  }

  > div button div {
    color: #f6f1e7;
    font-family: ${(props) => props.theme.fontGtAlpina},
      ${(props) => props.theme.fallBackFontFamily}, serif;
    font-weight: 300;
    font-size: ${toREM(12)}rem;
    letter-spacing: -0.0375em;
    line-height: ${toREM(16)}rem;
  }

  > div button img:last-child {
    display: none;
  }

  > div button::after {
    content: "";
    position: absolute;
    right: ${toVWMobile(16)}vw;
    top: 50%;
    transform: translateY(-50%);
    width: ${toVWMobile(16)}vw;
    height: ${toVWMobile(16)}vw;
    background-image: url(${footerMenuIcon});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    pointer-events: none;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    max-width: ${toVWDesktop(324)}vw;
    border-top: none;
    border-bottom: none;
    padding: 0;

    > div {
      width: 100%;
      gap: ${toVWDesktop(20)}vw;
    }

    > div button {
      box-sizing: border-box;
      width: 100%;
      border: 1px solid #f6f1e7;
      border-radius: ${toVWDesktop(431)}vw;
      padding: ${toVWDesktop(12)}vw ${toVWDesktop(16)}vw ${toVWDesktop(12)}vw
        ${toVWDesktop(24)}vw;
      min-height: ${toVWDesktop(44)}vw;
    }

    > div button:focus-visible {
      box-shadow: 0 0 0 ${toVWDesktop(2)}vw #f6f1e7;
      border-radius: ${toVWDesktop(431)}vw;
    }

    > div button img:last-child {
      display: none;
    }

    > div button::after {
      right: ${toVWDesktop(16)}vw;
      width: ${toVWDesktop(20)}vw;
      height: ${toVWDesktop(20)}vw;
    }

    > div button div {
      font-size: ${toREM(16)}rem;
      letter-spacing: -0.05em;
    }

    > div > div:first-child {
      font-size: ${toREM(20)}rem;
    }
  }
`;

const DesktopOnly = styled.div`
  display: none;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: block;
  }
`;
const MobileOnly = styled.div`
  display: block;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

export default FooterV2;
